/**
 * Grade12PopupGate - owns the 15s delay timer that decides when to
 * surface the Grade12Popup on /subjects.
 *
 * Why a delay: the popup felt intrusive on first login when it fired
 * the moment /subjects mounted. The user's flow is: pick a uni → pick
 * a course (or "só praticar") → land on /subjects → take ~15s to look
 * around → THEN we ask the cohort question.
 *
 * Conditions for the timer to start (all must be true):
 *   - the user is hydrated (parent passed a non-null user)
 *   - user.is_current_grade_12 is null/undefined (never answered)
 *   - per-student localStorage dismissal flag is not set
 *
 * Once the popup is shown OR dismissed, the localStorage flag is
 * marked permanently - we never re-pester. Students who skip can
 * answer later from /profile?section=detalhes.
 *
 * 2026-04-29: timer hardened against parent re-renders. Previously
 * a single useEffect with [user?.id, user?.is_current_grade_12] deps
 * worked in theory but in practice the parent's user object can mutate
 * (refresh after navigation, focus, etc.) which cleared the timer
 * before it could fire. Now we use a ref to track the SCHEDULED
 * fire-time and only schedule once per eligible window.
 *
 * 2026-04-29 (later): unconditional console.debug + dev escape hatch.
 * The popup repeatedly looked "broken" during QA because the dismissal
 * flag persists per-browser/per-student. To make verification cheap:
 *   - every gate decision logs a one-line reason to console.debug
 *     ("scheduled" / "skipped: answered" / "skipped: dismissed").
 *   - URL ?force_grade12_popup=1 clears the dismissal flag for the
 *     current student and treats is_current_grade_12 as null. Pure
 *     dev affordance - no harm in prod since users don't construct
 *     this URL by accident.
 */

import React, { useEffect, useRef, useState } from 'react'
import { Grade12Popup } from './Grade12Popup'

const POPUP_DELAY_MS = 15_000

interface UserShape {
  id: string
  is_current_grade_12?: boolean | null
}

export interface Grade12PopupGateProps {
  user: UserShape | null
  /** Called after the student answers (or skips). Receives the chosen
   *  branch so the parent can react - e.g. flip /subjects into the
   *  12ª view when the answer is 'yes'. Typically also refreshes
   *  /auth/me so derived fields (preferred_mode, etc.) update. */
  onAnswered: (chosen: 'yes' | 'no' | 'skip') => void | Promise<void>
}

const dismissalKey = (id: string) => `grade12_popup_dismissed_${id}`

/** Read URL flag without bringing react-router into this leaf component. */
function hasForceFlag(): boolean {
  if (typeof window === 'undefined') return false
  try {
    return new URLSearchParams(window.location.search).get('force_grade12_popup') === '1'
  } catch {
    return false
  }
}

/**
 * Per-student "first time we became eligible" key. We persist this in
 * sessionStorage so the 15s countdown is anchored to a wall-clock moment
 * rather than to React's effect lifecycle. Without this, React 18
 * StrictMode (enabled in main.tsx) double-invokes effects: the simulated
 * cleanup cancels the only setTimeout we scheduled, and the remount
 * early-returns because the "already scheduled" ref still says yes for
 * the same student id - so the timer is silently dead. With a timestamp
 * baseline, every mount just computes "how many ms left until popup
 * time" and schedules from there, idempotent across remounts/refreshes.
 */
const eligibleAtKey = (id: string) => `grade12_popup_eligible_at_${id}`

export const Grade12PopupGate: React.FC<Grade12PopupGateProps> = ({ user, onAnswered }) => {
  const [show, setShow] = useState(false)
  // Local-to-this-mount timer. Cleared on this mount's unmount so a
  // truly-gone component doesn't try to setShow on a ghost. The
  // sessionStorage baseline means a remount after StrictMode cleanup
  // can re-schedule the same (or a shorter) timer without losing time.
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(() => {
    const id = user?.id
    if (!id) {
      console.debug('[Grade12PopupGate] skipped: user not hydrated yet')
      return
    }

    const force = hasForceFlag()
    if (force) {
      try {
        localStorage.removeItem(dismissalKey(id))
        sessionStorage.removeItem(eligibleAtKey(id))
      } catch { /* */ }
      console.debug(`[Grade12PopupGate] force flag set for student=${id} - bypassing answered + dismissal checks`)
    }

    // Already answered: never schedule (unless force flag overrides).
    if (!force && user.is_current_grade_12 !== null && user.is_current_grade_12 !== undefined) {
      console.debug(`[Grade12PopupGate] skipped: is_current_grade_12=${user.is_current_grade_12} (already answered) for student=${id}`)
      return
    }

    // Permanently dismissed: never schedule (unless force flag).
    if (!force) {
      try {
        if (localStorage.getItem(dismissalKey(id))) {
          console.debug(`[Grade12PopupGate] skipped: localStorage dismissal flag set for student=${id} (use ?force_grade12_popup=1 to override)`)
          return
        }
      } catch {
        /* localStorage unavailable - fall through */
      }
    }

    // Compute the moment we first became eligible. If absent, stamp now.
    let eligibleAt = Number(sessionStorage.getItem(eligibleAtKey(id)) ?? 0)
    if (!eligibleAt) {
      eligibleAt = Date.now()
      try { sessionStorage.setItem(eligibleAtKey(id), String(eligibleAt)) } catch { /* */ }
    }
    const fireAt    = eligibleAt + POPUP_DELAY_MS
    const remaining = fireAt - Date.now()

    if (remaining <= 0) {
      console.debug(`[Grade12PopupGate] elapsed ${-remaining}ms past target for student=${id} - showing immediately`)
      setShow(true)
      return
    }

    console.debug(`[Grade12PopupGate] scheduled for student=${id} - fires in ${remaining}ms (anchor=${new Date(eligibleAt).toISOString()})`)
    timerRef.current = setTimeout(() => {
      console.debug(`[Grade12PopupGate] timer fired for student=${id} - showing popup`)
      setShow(true)
    }, remaining)

    // Per-mount cleanup - does NOT lose progress because the wall-clock
    // baseline lives in sessionStorage. A subsequent remount will
    // compute a fresh `remaining` and re-arm the timeout.
    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current)
        timerRef.current = null
      }
    }
  }, [user?.id, user?.is_current_grade_12])

  if (!show || !user?.id) return null

  return (
    <Grade12Popup
      studentId={user.id}
      onClose={async (chosen) => {
        setShow(false)
        await onAnswered(chosen)
      }}
    />
  )
}
